import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { Button, Field, Input, Modal, Select } from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDateTime } from '../../lib/format';
import ls from '../lockers/lockers.module.css';
import { CodeReveal } from './CodeReveal';
import { useChannels } from '../automations/AutomationsPage';
import { lockerOptionLabel, useLockerOptions } from './common';
import s from './operations.module.css';

type Created = components['schemas']['OperationCreated'];
const STEPS = ['Locker & compartiment', 'Destinatar & referință', 'Valabilitate'];

/** Wizardul de operațiune (spec §7): 3 pași, un singur `POST /operations`, codul afișat o singură dată. */
export function OperationWizard({
  onClose,
  lockerId: initialLocker = '',
}: {
  onClose: () => void;
  lockerId?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const lockers = useLockerOptions();
  const [step, setStep] = useState(0);
  const [lockerId, setLockerId] = useState(initialLocker);
  const [slotId, setSlotId] = useState('');
  const [title, setTitle] = useState('');
  const [reference, setReference] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [keyHours, setKeyHours] = useState<number | ''>('');
  const [validFrom, setValidFrom] = useState('');
  const [channel, setChannel] = useState<'none' | 'email' | 'sms'>('none');
  const channels = useChannels();
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Cheia de idempotență e fixată la intrarea în pasul 3: o reîncercare după o eroare de rețea nu creează două operațiuni.
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [created, setCreated] = useState<Created | null>(null);

  const detail = useQuery({
    queryKey: ['locker', lockerId],
    queryFn: () => unwrap(api.GET('/lockers/{id}', { params: { path: { id: lockerId } } })),
    enabled: !!lockerId,
  });
  const locker = detail.data;
  const freeSlots = useMemo(
    () =>
      (locker?.slots ?? [])
        .filter((x) => x.occupancy === 'free' && x.techState === 'active' && !x.doorOpen)
        .sort((a, b) => a.label.localeCompare(b.label, 'ro', { numeric: true })),
    [locker],
  );
  const slot = freeSlots.find((x) => x.id === slotId);
  const effectiveHours = keyHours === '' ? (locker?.rules.keyExpiryHours ?? 48) : keyHours;
  const from = validFrom ? new Date(validFrom) : new Date();
  const expires = new Date(from.getTime() + Number(effectiveHours) * 3600_000);

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/operations', {
          params: { header: { 'Idempotency-Key': idempotencyKey ?? undefined } },
          body: {
            lockerId,
            slotId,
            title: title.trim(),
            reference: reference.trim(),
            recipient: {
              name: name.trim(),
              email: email.trim() || null,
              phone: phone.trim() || null,
            },
            notes: notes.trim() || undefined,
            keyHours: keyHours === '' ? undefined : Number(keyHours),
            validFrom: validFrom ? new Date(validFrom).toISOString() : undefined,
            channel,
          },
        }),
      ),
    onSuccess: (d) => {
      setCreated(d);
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['count', 'operations'] });
      void qc.invalidateQueries({ queryKey: ['locker', lockerId] });
      void qc.invalidateQueries({ queryKey: ['access-grants'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (step === 0) {
      if (!lockerId) next.lockerId = 'Alege un locker';
      else if (locker && locker.status !== 'online')
        next.lockerId = 'Lockerul nu este online; nu acceptă operațiuni noi';
      if (!slotId) next.slotId = 'Alege un compartiment liber';
    }
    if (step === 1) {
      if (title.trim().length < 2) next.title = 'Minimum 2 caractere';
      if (!reference.trim()) next.reference = 'Obligatoriu';
      if (name.trim().length < 2) next.name = 'Minimum 2 caractere';
      if (!email.trim() && !phone.trim()) next.contact = 'E-mail sau telefon, cel puțin unul';
      if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()))
        next.email = 'E-mail invalid';
      if (phone.trim() && !/^\+?[0-9 ]{6,20}$/.test(phone.trim()))
        next.phone = 'Doar cifre, spații și prefixul +';
    }
    if (step === 2) {
      const h = Number(effectiveHours);
      if (!Number.isInteger(h) || h < 1 || h > 168) next.keyHours = 'Între 1 și 168 de ore';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const next = () => {
    if (!validate()) return;
    if (step === 1 && !idempotencyKey) setIdempotencyKey(crypto.randomUUID());
    setStep((x) => x + 1);
  };

  if (created) {
    return (
      <CodeReveal
        code={created.accessGrant.code}
        purpose={created.accessGrant.purpose}
        expiresAt={created.accessGrant.expiresAt}
        operationId={created.operation.id}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      title="Operațiune nouă"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          {step > 0 && <Button onClick={() => setStep((x) => x - 1)}>Înapoi</Button>}
          {step < STEPS.length - 1 ? (
            <Button variant="primary" onClick={next}>
              Continuă
            </Button>
          ) : (
            <Button
              variant="primary"
              icon="key"
              onClick={() => validate() && create.mutate()}
              busy={create.isPending}
            >
              Emite cheia de depunere
            </Button>
          )}
        </>
      }
    >
      <div className={ls.stepper}>
        {STEPS.map((label, i) => (
          <div
            key={label}
            className={[ls.step, i === step && ls.stepActive, i < step && ls.stepDone]
              .filter(Boolean)
              .join(' ')}
          >
            <i>{i < step ? '✓' : i + 1}</i>
            {label}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="stack">
          <Field label="Locker" error={errors.lockerId} htmlFor="op-locker">
            <Select
              id="op-locker"
              value={lockerId}
              onChange={(e) => {
                setLockerId(e.target.value);
                setSlotId('');
              }}
            >
              <option value="">Alege…</option>
              {lockers.data?.items.map((l) => (
                <option key={l.id} value={l.id} disabled={l.status !== 'online'}>
                  {lockerOptionLabel(l)}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Compartiment liber"
            hint={
              locker
                ? `${freeSlots.length} libere și funcționale din ${locker.slots.length}`
                : undefined
            }
            error={errors.slotId}
            htmlFor="op-slot"
          >
            <Select
              id="op-slot"
              value={slotId}
              disabled={!locker}
              onChange={(e) => setSlotId(e.target.value)}
            >
              <option value="">Alege…</option>
              {freeSlots.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label} · {x.size} · {x.dimensionsMm.width}×{x.dimensionsMm.height}×
                  {x.dimensionsMm.depth} mm
                </option>
              ))}
            </Select>
          </Field>
          <p className="small muted">
            La emitere compartimentul este rezervat; ocuparea se confirmă după închiderea ușii și
            confirmarea de la kiosk.
          </p>
        </div>
      )}

      {step === 1 && (
        <div className="stack">
          <div className={ls.formGrid}>
            <Field
              label="Titlu"
              hint="Ce se depune (ex. colet AWB, cheie apartament)"
              error={errors.title}
              htmlFor="op-title"
            >
              <Input
                id="op-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                invalid={!!errors.title}
              />
            </Field>
            <Field
              label="Referință"
              hint="Comandă / AWB / rezervare / contract"
              error={errors.reference}
              htmlFor="op-ref"
            >
              <Input
                id="op-ref"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={64}
                invalid={!!errors.reference}
              />
            </Field>
            <Field label="Destinatar" error={errors.name} htmlFor="op-name">
              <Input
                id="op-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                invalid={!!errors.name}
              />
            </Field>
            <Field label="E-mail" error={errors.email ?? errors.contact} htmlFor="op-email">
              <Input
                id="op-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                invalid={!!(errors.email || errors.contact)}
              />
            </Field>
            <Field
              label="Telefon"
              hint="Ex. +40 700 000 000"
              error={errors.phone}
              htmlFor="op-phone"
            >
              <Input
                id="op-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                invalid={!!(errors.phone || errors.contact)}
              />
            </Field>
          </div>
          <Field label="Instrucțiuni interne" htmlFor="op-notes">
            <textarea
              id="op-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={2000}
              style={{
                width: '100%',
                border: '1px solid var(--line)',
                borderRadius: 9,
                padding: 10,
                font: 'inherit',
              }}
            />
          </Field>
        </div>
      )}

      {step === 2 && (
        <div className="stack">
          <div className={ls.formGrid}>
            <Field
              label="Valabilitatea cheii (ore)"
              hint={`Implicit regula lockerului: ${locker?.rules.keyExpiryHours ?? '…'} h · maximum 168`}
              error={errors.keyHours}
              htmlFor="op-hours"
            >
              <Input
                id="op-hours"
                type="number"
                min={1}
                max={168}
                value={keyHours}
                placeholder={String(locker?.rules.keyExpiryHours ?? '')}
                onChange={(e) => setKeyHours(e.target.value === '' ? '' : Number(e.target.value))}
                invalid={!!errors.keyHours}
              />
            </Field>
            <Field label="Activă de la" hint="Gol = acum" htmlFor="op-from">
              <Input
                id="op-from"
                type="datetime-local"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            </Field>
          </div>
          <Field
            label="Trimite codul destinatarului"
            hint={
              channels.data && !channels.data.smsEnabled
                ? 'SMS-ul este dezactivat global; e-mailul este canalul implicit'
                : 'Codul pleacă o singură dată, imediat după emitere'
            }
            htmlFor="op-channel"
          >
            <Select
              id="op-channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value as 'none' | 'email' | 'sms')}
            >
              <option value="none">Nu, îl transmit eu</option>
              <option
                value="email"
                disabled={!email.trim() || channels.data?.emailEnabled === false}
              >
                Prin e-mail{email.trim() ? ` (${email.trim()})` : ' (fără adresă)'}
              </option>
              {channels.data?.smsEnabled && (
                <option value="sms" disabled={!phone.trim()}>
                  Prin SMS{phone.trim() ? ` (${phone.trim()})` : ' (fără telefon)'}
                </option>
              )}
            </Select>
          </Field>
          <dl className={s.pairs}>
            <dt>Locker</dt>
            <dd>{locker?.name}</dd>
            <dt>Compartiment</dt>
            <dd>{slot ? `${slot.label} · ${slot.size}` : '—'}</dd>
            <dt>Titlu / referință</dt>
            <dd>
              {title} · <span className="mono">{reference}</span>
            </dd>
            <dt>Destinatar</dt>
            <dd>
              {name} · {[email, phone].filter(Boolean).join(' · ')}
            </dd>
            <dt>Cheia expiră</dt>
            <dd>{fmtDateTime(expires.toISOString())}</dd>
          </dl>
          <p className="small muted">
            Codul de depunere apare o singură dată, imediat după emitere. Nu este trimis automat
            destinatarului în acest sub-proiect (notificările vin cu integrările).
          </p>
        </div>
      )}
    </Modal>
  );
}
